import type { StylesConfig } from 'react-select';

import Configs from '@/Configs';

import type { CountrySelectOption } from './CountrySelect';

/** Indigo-600 — matches PLG signup primary / KincodeButton colorHex. */
const PLG_BRAND = '#4F46E5';
/** Indigo-500 focus border (Tailwind indigo-500). */
const PLG_FOCUS_BORDER = '#6366F1';
const PLG_FOCUS_RING = '0 0 0 4px rgba(99, 102, 241, 0.1)';
const PLG_BORDER = '#E2E8F0'; // slate-200
const PLG_BG = '#F8FAFC'; // slate-50
const PLG_TEXT = '#020617'; // slate-950
const PLG_MUTED = '#94A3B8'; // slate-400
const PLG_OPTION_HOVER = 'rgba(79, 70, 229, 0.08)';
const PLG_OPTION_SELECTED = 'rgba(79, 70, 229, 0.14)';

/**
 * react-select styles for public PLG signup — matches NewCompanyOnboarding input surfaces.
 */
export const plgCountrySelectStyles: StylesConfig<CountrySelectOption, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '48px',
    height: '48px',
    borderRadius: '0.75rem',
    backgroundColor: PLG_BG,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: state.isFocused ? PLG_FOCUS_BORDER : PLG_BORDER,
    boxShadow: state.isFocused ? PLG_FOCUS_RING : 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': {
      borderColor: state.isFocused ? PLG_FOCUS_BORDER : '#CBD5E1',
      backgroundColor: PLG_BG,
      boxShadow: state.isFocused ? PLG_FOCUS_RING : 'none',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 1rem',
    height: '46px',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    color: PLG_TEXT,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: PLG_TEXT,
    fontSize: '0.875rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: PLG_MUTED,
    fontSize: '0.875rem',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '46px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: PLG_MUTED,
    padding: '0 0.75rem',
    '&:hover': { color: PLG_BRAND },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: PLG_MUTED,
    padding: '0 0.25rem',
    '&:hover': { color: PLG_BRAND },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.75rem',
    border: `1px solid ${PLG_BORDER}`,
    boxShadow: '0 10px 40px -12px rgba(15, 23, 42, 0.25)',
    overflow: 'hidden',
    zIndex: 30,
  }),
  /** Portaled menu must sit above the PLG sticky CTA bar (z-10) and panel. */
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 10050,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.25rem',
    maxHeight: '240px',
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    color: PLG_TEXT,
    backgroundColor: state.isSelected
      ? PLG_OPTION_SELECTED
      : state.isFocused
        ? PLG_OPTION_HOVER
        : 'transparent',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: PLG_OPTION_SELECTED,
    },
  }),
};

/** Default Admin / widget appearance (existing Configs.widgetDropdownStyle). */
export const defaultCountrySelectStyles: StylesConfig<CountrySelectOption, false> =
  Configs.widgetDropdownStyle as StylesConfig<CountrySelectOption, false>;
